import type { ComponentType } from 'react'
import clsx from 'clsx'

import { useDataStatus } from '../lib/data'
import { useTheme } from '../lib/useTheme'
import { THEME_CHOICES, type ThemeChoice } from '../lib/theme'
import { SCALE_CHOICES, type ScaleChoice } from '../lib/scale'
import { SettingsNav } from '../components/connections/SettingsNav'
import { Section } from '../components/connections/parts'
import { Monitor } from '../components/connections/icons'
import { Moon, Sun, type IconProps } from '../components/icons'
import { BuildBadge } from '../components/HeaderBand'

// ============================================================================
// APPEARANCE — the second settings screen, and the home of the theme control.
//
// NO MOCKUP SOURCE. The handoff's settings nav lists `Appearance` as one of six
// rows and draws none of them; dark mode is an owner request on top of a
// one-appearance design (2026-08-26). So this screen is built out of the
// Connections screen's own parts — the same 236px nav, the same header, the same
// `Section` rule — rather than inventing a second settings idiom. Placement is
// REVERSIBLE and flagged as such: the alternative was a control in the rail,
// which would have made an 8-item rail a 9-item one for a preference nobody
// changes twice a week.
//
// The screen says three true things and no more: what the theme is, where the
// choice is kept, and what it does to browser extensions that recolour pages.
// ============================================================================

interface Option {
  choice: ThemeChoice
  label: string
  icon: ComponentType<IconProps>
  /** What picking it actually does. Never a restatement of the label. */
  detail: string
}

const OPTIONS: Record<ThemeChoice, Option> = {
  light: { choice: 'light', label: 'Light', icon: Sun, detail: 'The daytime palette, always.' },
  dark: { choice: 'dark', label: 'Dark', icon: Moon, detail: 'The night palette, always.' },
  system: {
    choice: 'system',
    label: 'System',
    icon: Monitor,
    detail: 'Follows your machine.',
  },
}

interface SizeOption {
  choice: ScaleChoice
  label: string
  /** What picking it actually does. Never a restatement of the label. */
  detail: string
}

// LARGE IS THE DEFAULT (owner request, 2026-08-26): "the whole UI feels too tiny," and he has been
// pressing Ctrl+= on every fresh load to compensate. This makes that permanent rather than
// something he re-applies every session — see scale.ts.
const SIZE_OPTIONS: Record<ScaleChoice, SizeOption> = {
  handoff: {
    choice: 'handoff',
    label: 'Handoff (100%)',
    detail: 'The mockups at their designed size.',
  },
  comfortable: {
    choice: 'comfortable',
    label: 'Comfortable (115%)',
    detail: 'A step up, if 100% still reads small.',
  },
  large: {
    choice: 'large',
    label: 'Large (130%)',
    detail: 'The default — about what Ctrl+= already gave you.',
  },
}

export function Appearance() {
  const { choice, resolved, systemIsDark, setChoice, scale, setScale } = useTheme()
  const dataStatus = useDataStatus()

  return (
    <div className="flex min-h-0 flex-1">
      <SettingsNav
        active="appearance"
        tone={null}
        toneReason={null}
        demoMode={dataStatus.data?.mode === 'demo'}
      />

      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto">
        <header className="flex flex-none items-center gap-[14px] border-b border-line bg-surface px-[30px] pb-4 pt-5">
          <div className="flex flex-col gap-[5px]">
            <h1 className="text-[16px] font-semibold leading-none tracking-[-0.01em]">Appearance</h1>
            <p className="text-[11.5px] font-normal leading-none text-microcopy">
              Currently {resolved} · your machine is asking for {systemIsDark ? 'dark' : 'light'}
            </p>
          </div>
          <BuildBadge className="ml-auto" />
        </header>

        <div className="flex flex-col gap-5 px-[30px] pb-[30px] pt-[18px]">
          <Section label="Theme">
            <div
              role="radiogroup"
              aria-label="Theme"
              className="flex gap-3 rounded-card border border-line bg-surface p-3 shadow-card"
            >
              {THEME_CHOICES.map((id) => (
                <ThemeOption
                  key={id}
                  option={OPTIONS[id]}
                  selected={choice === id}
                  // Only 'system' has a second thing to say, and only while it
                  // is selected: what it resolved to right now.
                  resolvedNote={
                    id === 'system' && choice === 'system' ? `Dark right now: ${systemIsDark ? 'yes' : 'no'}` : null
                  }
                  onPick={() => setChoice(id)}
                />
              ))}
            </div>

            <p className="text-[11.5px] font-normal leading-[1.5] text-ink-3 text-pretty">
              The choice is kept in this browser only. It does not sync to your phone, and signing
              out does not clear it — nothing about it reaches the server.
            </p>
          </Section>

          <Section label="Size">
            <div
              role="radiogroup"
              aria-label="Size"
              className="flex gap-3 rounded-card border border-line bg-surface p-3 shadow-card"
            >
              {SCALE_CHOICES.map((id) => (
                <SizeOptionTile
                  key={id}
                  option={SIZE_OPTIONS[id]}
                  selected={scale === id}
                  onPick={() => setScale(id)}
                />
              ))}
            </div>

            <p className="text-[11.5px] font-normal leading-[1.5] text-ink-3 text-pretty">
              Applied with the browser&rsquo;s own zoom, app-wide — the same trick as pressing
              Ctrl+=, just remembered for you. Kept in this browser only, same as the theme choice
              above.
            </p>
          </Section>

          <Section label="Browser extensions">
            <div className="flex items-start gap-[11px] rounded-row border border-line bg-surface px-[15px] py-[13px]">
              <span className="mt-[6px] size-[5px] flex-none rounded-full bg-microcopy" aria-hidden />
              <p className="min-w-0 flex-1 text-[11.5px] font-normal leading-[1.5] text-ink-3 text-pretty">
                Extensions that invert page colours — Dark Reader and the like — are asked to stand
                down on this app, because it now ships its own dark theme. What you see here is this
                palette, not an extension&rsquo;s guess at one.
              </p>
            </div>
          </Section>
        </div>
      </div>
    </div>
  )
}

/**
 * One option. A real radio input, visually replaced: the tile has to be
 * reachable with the arrow keys and announce itself as a radio, and rebuilding
 * that on a <button> is how a settings screen loses its keyboard.
 */
function ThemeOption({
  option,
  selected,
  resolvedNote,
  onPick,
}: {
  option: Option
  selected: boolean
  resolvedNote: string | null
  onPick: () => void
}) {
  const { label, detail, icon: Icon } = option
  return (
    <label
      className={clsx(
        'flex flex-1 cursor-pointer flex-col gap-[9px] rounded-tile border px-[15px] py-[13px]',
        'transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)]',
        // The ring belongs to :focus-visible only (motion-brief §2 — keyboard-only, never on a
        // mouse click). A plain `:focus-within` fires on ANY focus, including the click that
        // selects the tile, which is exactly the double ring the owner reported: the browser's
        // default outline stacked on the card's own selected border. `has-[:focus-visible]`
        // narrows it back to keyboard focus, and the colour is the tokenized ring, not `ink`.
        'has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-focus-ring',
        selected ? 'border-ink bg-surface-2' : 'border-line bg-surface hover:bg-surface-4',
      )}
    >
      <input
        type="radio"
        name="theme"
        value={option.choice}
        checked={selected}
        onChange={onPick}
        // NOT TIDINESS. Chrome restores radio-button state across a reload, and a restored
        // radio fires `change` — which would quietly rewrite the stored theme to whatever was
        // last checked in this tab, overwriting the real preference on every refresh. The
        // setting has one source of truth and it is localStorage, not form-state restoration.
        autoComplete="off"
        className="sr-only"
      />
      <div className="flex items-center gap-[9px]">
        <Icon size={15} strokeWidth={1.8} className={selected ? 'text-ink' : 'text-muted'} />
        <span
          className={clsx(
            'text-[12.5px] leading-none',
            selected ? 'font-semibold text-ink' : 'font-medium text-ink-2',
          )}
        >
          {label}
        </span>
        {selected && (
          <span className="ml-auto size-[6px] flex-none rounded-full bg-success-dot" aria-hidden />
        )}
      </div>
      <span className="text-[10.5px] font-normal leading-[1.45] text-microcopy">{detail}</span>
      {resolvedNote && (
        <span className="text-[10.5px] font-normal leading-[1.45] text-muted">{resolvedNote}</span>
      )}
    </label>
  )
}

/**
 * One size option. Same tile idiom as ThemeOption above — a real radio input, visually replaced —
 * minus the icon column: the handoff draws no icon set for a size control (there is no mockup for
 * this screen at all; see the file banner), and inventing one would be inventing scope for a
 * control that is three text labels and a selected dot.
 */
function SizeOptionTile({
  option,
  selected,
  onPick,
}: {
  option: SizeOption
  selected: boolean
  onPick: () => void
}) {
  const { label, detail } = option
  return (
    <label
      className={clsx(
        'flex flex-1 cursor-pointer flex-col gap-[9px] rounded-tile border px-[15px] py-[13px]',
        'transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)]',
        // The ring belongs to :focus-visible only (motion-brief §2 — keyboard-only, never on a
        // mouse click). A plain `:focus-within` fires on ANY focus, including the click that
        // selects the tile, which is exactly the double ring the owner reported: the browser's
        // default outline stacked on the card's own selected border. `has-[:focus-visible]`
        // narrows it back to keyboard focus, and the colour is the tokenized ring, not `ink`.
        'has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-focus-ring',
        selected ? 'border-ink bg-surface-2' : 'border-line bg-surface hover:bg-surface-4',
      )}
    >
      <input
        type="radio"
        name="scale"
        value={option.choice}
        checked={selected}
        onChange={onPick}
        // Same reasoning as the theme radio above: the setting's one source of truth is
        // localStorage, not Chrome's own form-state restoration across a reload.
        autoComplete="off"
        className="sr-only"
      />
      <div className="flex items-center gap-[9px]">
        <span
          className={clsx(
            'text-[12.5px] leading-none',
            selected ? 'font-semibold text-ink' : 'font-medium text-ink-2',
          )}
        >
          {label}
        </span>
        {selected && (
          <span className="ml-auto size-[6px] flex-none rounded-full bg-success-dot" aria-hidden />
        )}
      </div>
      <span className="text-[10.5px] font-normal leading-[1.45] text-microcopy">{detail}</span>
    </label>
  )
}
